/**
 * VIPGIFT free-gift automation.
 *
 * Shopify's Buy X Get Y discounts only discount a qualifying item that is ALREADY in
 * the cart — they cannot insert one. So VIPGIFT on its own requires the customer to
 * add the infuser manually. This adds it for them when the code is applied and the
 * cart qualifies, and removes it again if the cart later drops below the threshold.
 *
 * The removal half matters more than the add: if the gift stays in a cart that no
 * longer qualifies, the BXGY stops applying and the customer is silently charged for
 * an infuser they never chose.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** Tea Basket Infuser – Blue */
const GIFT_VARIANT_ID = 45215374934039;
const CODE = 'VIPGIFT';
const THRESHOLD_CENTS = 7500;

const DISMISSED_KEY = 'vipgift:dismissed';
const PRESENT_KEY = 'vipgift:present';

const nativeFetch = window.fetch.bind(window);
const CART_MUTATION = /\/cart\/(add|change|update|clear)/;

let running = false;

const readFlag = (key) => {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (key, on) => {
  try {
    if (on) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    /* private browsing */
  }
};

const codeApplied = (cart) =>
  (cart.discount_codes || []).some((entry) => String(entry.code).toUpperCase() === CODE);

const giftLine = (cart) => (cart.items || []).find((item) => item.variant_id === GIFT_VARIANT_ID);

/** Cart value excluding the gift itself, at pre-discount prices. */
const qualifyingTotal = (cart) =>
  (cart.items || []).reduce(
    (sum, item) =>
      item.variant_id === GIFT_VARIANT_ID ? sum : sum + (item.original_line_price ?? item.line_price ?? 0),
    0
  );

/**
 * Mutate the cart and let the theme re-render, using the same CartLinesUpdateEvent
 * handshake the bundle picker uses.
 */
async function mutateCart(action, url, body) {
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [{ merchandiseId: String(GIFT_VARIANT_ID), quantity: action === 'add' ? 1 : 0 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`VIPGIFT ${action} failed`);

    const cart = await (await nativeFetch('/cart.js')).json();
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
    return cart;
  } catch (error) {
    deferred.reject(error);
    return null;
  }
}

async function evaluate(cart) {
  if (!cart || running) return;

  const gift = giftLine(cart);
  const qualifies = codeApplied(cart) && qualifyingTotal(cart) >= THRESHOLD_CENTS;

  // Customer deleted the gift while still qualifying — respect that rather than
  // re-adding it on every cart change.
  if (qualifies && !gift && readFlag(PRESENT_KEY)) writeFlag(DISMISSED_KEY, true);
  if (gift) writeFlag(DISMISSED_KEY, false);
  writeFlag(PRESENT_KEY, Boolean(gift));

  running = true;
  try {
    if (qualifies && !gift && !readFlag(DISMISSED_KEY)) {
      const updated = await mutateCart('add', '/cart/add.js', {
        items: [{ id: GIFT_VARIANT_ID, quantity: 1 }],
      });
      if (updated) writeFlag(PRESENT_KEY, true);
    } else if (gift && !qualifies) {
      await mutateCart('remove', '/cart/change.js', { id: gift.key, quantity: 0 });
      writeFlag(PRESENT_KEY, false);
    }
  } finally {
    running = false;
  }
}

async function refresh() {
  if (running) return;
  try {
    const cart = await (await nativeFetch('/cart.js')).json();
    await evaluate(cart);
  } catch {
    /* cart unavailable — leave it alone */
  }
}

// Re-evaluate after any cart mutation the theme performs. Deferred a tick so the
// theme finishes its own handling of the response first.
window.fetch = function (input, init) {
  const url = typeof input === 'string' ? input : input?.url || '';
  const result = nativeFetch(input, init);

  if (CART_MUTATION.test(url)) {
    result
      .then(() => {
        setTimeout(refresh, 0);
      })
      .catch(() => {});
  }

  return result;
};

refresh();
